import { Modal } from '@carbon/react';

interface UnsavedChangesModalProps {
  /** One sentence naming what is about to be lost. */
  message: string;
  /**
   * Cancel, the close button and Escape all land here. Escape calls the onKeep from the render
   * that mounted this modal (Carbon captures onRequestClose once, carbon#20452), so keep it
   * free of state that changes while the modal is up.
   */
  onKeep: () => void;
  /** "Yes, Discard", the only path that loses the work. */
  onDiscard: () => void;
}

/**
 * Asks before unsaved work is thrown away.
 *
 * Mount it only while asking; it is always open. Enter is deliberately not wired to the
 * discard: Carbon focuses Cancel on a danger modal, and `shouldSubmitOnEnter` fires the
 * primary action from whichever button has focus, so Enter on a focused Cancel would have
 * discarded the work.
 *
 * Closed on purpose: no size, className or other passthrough to Carbon, so every unsaved-work
 * prompt looks and behaves the same. To confirm a delete, use components/destroy-confirmation,
 * which is controlled through `open`, passes the rest through to Carbon and portals to body.
 *
 * @param {UnsavedChangesModalProps} props - Component props.
 * @return {JSX.Element} The confirmation modal.
 */
export const UnsavedChangesModal = ({
  message,
  onKeep,
  onDiscard,
}: UnsavedChangesModalProps): JSX.Element => (
  <Modal
    open
    danger
    modalHeading="Unsaved Changes"
    primaryButtonText="Yes, Discard"
    secondaryButtonText="Cancel"
    onRequestClose={onKeep}
    onSecondarySubmit={onKeep}
    onRequestSubmit={onDiscard}
  >
    <p>{message}</p>
  </Modal>
);
